// Desktop (Tauri) bridge. The same React app runs on the web and inside the Tauri webview;
// everything here is a no-op on the web (guarded by isTauri()) so the web bundle is
// unaffected. `@tauri-apps/api` is dynamically imported only when actually invoking a native
// command, keeping it out of the main chunk.
import { backendOrigin, getSessionToken } from "../api/client";
import { b64encode, type UnlockedKeys } from "../crypto/vault";

/** True when running inside the Tauri desktop webview. */
export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

/** Add an https:// scheme when a stored URL/host has none, so it isn't treated as a relative path. */
export function normalizeUrl(raw: string): string {
  const u = raw.trim();
  if (!u) return u;
  // Already has a scheme (http:, https:, mailto:, ssh:, vnc:, …) → leave it.
  if (/^[a-z][a-z0-9+.-]*:/i.test(u)) return u;
  return `https://${u}`;
}

/** Open an external URL: the system browser on desktop (Tauri webview can't via window.open),
 *  a new tab on web. The URL is scheme-normalized first so bare hosts still open. */
export async function openExternal(raw: string): Promise<void> {
  const url = normalizeUrl(raw);
  if (!url) return;
  if (isTauri()) {
    const { openUrl } = await import("@tauri-apps/plugin-opener");
    await openUrl(url);
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/** Hand the native side the unlocked private key + auth context (Launch > Reveal, §13). */
export async function initSession(keys: UnlockedKeys): Promise<void> {
  if (!isTauri()) return;
  const token = getSessionToken();
  if (!token) return;
  await invoke("init_session", {
    baseUrl: backendOrigin() || "https://vault.itgrow.cz",
    token,
    privateKeyB64: b64encode(keys.privateKey),
  });
}

/** Drop the native session (auto-lock / sign-out). */
export async function lockSession(): Promise<void> {
  if (!isTauri()) return;
  try {
    await invoke("lock_session");
  } catch {
    /* ignore */
  }
}

// --- SSH launch (spec §13) ---

/** Info returned when an SSH session opens. */
export interface LaunchInfo {
  session_id: string;
  fingerprint: string; // "SHA256:..." — offer to pin on first connect
}

/** Decrypt an SSH item natively and open a session (credentials never reach JS). */
export async function launchSsh(itemId: string): Promise<LaunchInfo> {
  return invoke<LaunchInfo>("launch_ssh", { itemId });
}

/** Ad-hoc SSH params typed by the user (no saved item). Password is passed straight to the
 * native command and not retained on the JS side. */
export interface AdhocSshParams {
  host: string;
  port: number;
  username: string;
  password: string;
}

/** Open an SSH session to a typed host/credentials (quick-connect). Host-key is trust-on-first-use. */
export async function launchSshAdhoc(p: AdhocSshParams): Promise<LaunchInfo> {
  return invoke<LaunchInfo>("launch_ssh_adhoc", {
    host: p.host,
    port: p.port,
    username: p.username,
    password: p.password,
  });
}

/** Send keystrokes to a live session. */
export async function sshWrite(sessionId: string, data: string): Promise<void> {
  await invoke("ssh_write", { sessionId, data: btoa(data) });
}

/** Resize the remote PTY. */
export async function sshResize(sessionId: string, cols: number, rows: number): Promise<void> {
  await invoke("ssh_resize", { sessionId, cols, rows });
}

/** Close a live session. */
export async function sshClose(sessionId: string): Promise<void> {
  try {
    await invoke("ssh_close", { sessionId });
  } catch {
    /* ignore */
  }
}

// --- RDP launch (native OS client; spec §13) ---
// There is no embedded RDP renderer — it could not route through an RD Gateway. Windows opens a
// native window hosting the MsRdpClient ActiveX control (full gateway, single-credential reuse);
// macOS hands a .rdp to Microsoft Remote Desktop. Credentials are decrypted natively and never
// reach JS. Launch is fire-and-forget — the session lives in its own OS window, not an app tab.

/** Launch an item with the platform's native RDP client (gateway-capable). */
export async function launchRdp(itemId: string): Promise<void> {
  await invoke("launch_rdp", { itemId });
}

/** Windows fallback: open the item in mstsc.exe via a DPAPI-protected .rdp (gateway directives). */
export async function launchRdpMstsc(itemId: string): Promise<void> {
  await invoke("launch_rdp_mstsc", { itemId });
}

/** Ad-hoc RDP params typed by the user (no saved item). */
export interface AdhocRdpParams {
  host: string;
  port: number;
  domain: string;
  username: string;
  password: string;
}

/** Quick-connect: launch the native RDP client for typed host/credentials (no saved item). */
export async function launchRdpAdhoc(p: AdhocRdpParams): Promise<void> {
  await invoke("launch_rdp_adhoc", {
    fields: {
      host: p.host,
      port: String(p.port),
      domain: p.domain,
      username: p.username,
      password: p.password,
    },
  });
}

// --- Embedded RDP (IronRDP, in-app tab, cross-platform incl. macOS; spec §13/§23) ---
// Renders the remote desktop in-app: decoded dirty-rect RGBA frames arrive via `rdp:frame:<id>`
// events; input/resize/clipboard go back through the commands below. Credentials are decrypted
// natively and never reach JS. Coexists with the native launch above (the full-fidelity fallback).

/** A decoded dirty rectangle pushed from the native session (`rdp:frame:<id>`). */
export interface RdpFrame {
  x: number;
  y: number;
  w: number;
  h: number;
  data: string; // base64 RGBA8, w*h*4 bytes
}

/** Input event sent to the native session; `kind` matches the Rust `RdpInputDto` tag. */
export type RdpInputEvent =
  | { kind: "mouse_move"; x: number; y: number }
  | { kind: "mouse_button"; button: number; down: boolean }
  | { kind: "mouse_wheel"; vertical: boolean; delta: number }
  | { kind: "key"; scancode: number; down: boolean };

/** Open an embedded RDP session for a saved item at the given canvas size. */
export async function launchRdpEmbedded(itemId: string, width: number, height: number): Promise<LaunchInfo> {
  return invoke<LaunchInfo>("launch_rdp_embedded", { itemId, width, height });
}

/** Open an embedded RDP session to typed host/credentials (quick-connect). TOFU server cert. */
export async function launchRdpAdhocEmbedded(p: AdhocRdpParams, width: number, height: number): Promise<LaunchInfo> {
  return invoke<LaunchInfo>("launch_rdp_adhoc_embedded", {
    fields: {
      host: p.host,
      port: String(p.port),
      domain: p.domain,
      username: p.username,
      password: p.password,
    },
    width,
    height,
  });
}

/** Open an embedded RDP session from a decrypted field map (offline-resilient). */
export async function launchRdpEmbeddedParams(
  fields: Record<string, string>,
  width: number,
  height: number,
  itemId?: string,
): Promise<LaunchInfo> {
  return invoke<LaunchInfo>("launch_rdp_embedded_params", { fields, width, height, itemId });
}

/** Send a mouse/keyboard event to a live embedded RDP session. */
export async function rdpInput(sessionId: string, event: RdpInputEvent): Promise<void> {
  await invoke("rdp_input", { sessionId, event });
}

/** Offer local clipboard text to the remote (so it can be pasted inside the session). */
export async function rdpSetClipboard(sessionId: string, text: string): Promise<void> {
  await invoke("rdp_set_clipboard", { sessionId, text });
}

/** Close a live embedded RDP session. */
export async function rdpClose(sessionId: string): Promise<void> {
  try {
    await invoke("rdp_close", { sessionId });
  } catch {
    /* session already gone — ignore */
  }
}

// --- Params-based launch (offline-resilient, §15) ---
// These pass ALREADY-DECRYPTED fields (from an in-memory online item OR the offline snapshot) to
// native, so launch never re-fetches from the server — works offline and survives a mid-session
// connectivity drop. The webview already holds these plaintext fields to render the item.

/** Open an SSH session from a decrypted field map. `itemId` only drives a best-effort audit. */
export async function launchSshParams(fields: Record<string, string>, itemId?: string): Promise<LaunchInfo> {
  return invoke<LaunchInfo>("launch_ssh_params", { fields, itemId });
}

/** Native RDP launch from a decrypted field map (offline-resilient). */
export async function launchRdpParams(fields: Record<string, string>, itemId?: string): Promise<void> {
  await invoke("launch_rdp_params", { fields, itemId });
}

/** SCP upload from a decrypted SSH field map. */
export async function scpUploadParams(fields: Record<string, string>, localPath: string, remotePath: string, itemId?: string): Promise<void> {
  await invoke("scp_upload_params", { fields, localPath, remotePath, itemId });
}

/** SCP download from a decrypted SSH field map. */
export async function scpDownloadParams(fields: Record<string, string>, remotePath: string, localPath: string, itemId?: string): Promise<void> {
  await invoke("scp_download_params", { fields, remotePath, localPath, itemId });
}

/** Read the desktop's local settings blob (returns {} on web or if unset). */
export async function localSettingsGet(): Promise<Record<string, unknown>> {
  if (!isTauri()) return {};
  try {
    return await invoke<Record<string, unknown>>("local_settings_get");
  } catch {
    return {};
  }
}

/** Persist the desktop's local settings blob (no-op on web). */
export async function localSettingsSet(value: Record<string, unknown>): Promise<void> {
  if (!isTauri()) return;
  try {
    await invoke("local_settings_set", { value });
  } catch {
    /* best-effort */
  }
}

/** Upload a local file to a remote path on an SSH item's host (basic SCP, §13). */
export async function scpUpload(itemId: string, localPath: string, remotePath: string): Promise<void> {
  await invoke("scp_upload", { itemId, localPath, remotePath });
}

/** Download a remote file from an SSH item's host to a local path (basic SCP, §13). */
export async function scpDownload(itemId: string, remotePath: string, localPath: string): Promise<void> {
  await invoke("scp_download", { itemId, remotePath, localPath });
}

// --- Offline read-only cache (§15, desktop only) ---

export interface OfflineStatus {
  exists: boolean;
  built_at?: number;
  expired?: boolean;
}

/**
 * Arm the offline cache right after unlock: builds a fresh snapshot, wrapping the cache key under
 * the master key (passed once, transiently) and the hardware keystore. Call only on desktop when
 * the device has offline enabled.
 */
export async function offlineArm(masterKeyB64: string, kdfSaltB64: string): Promise<void> {
  await invoke("offline_arm", { masterKeyB64, kdfSaltB64 });
}

/** Manually refresh the offline snapshot under the retained cache key ("Rebuild now" fallback). */
export async function offlineRebuild(): Promise<void> {
  await invoke("offline_rebuild");
}

/** Current offline cache freshness. */
export async function offlineStatus(): Promise<OfflineStatus> {
  return invoke<OfflineStatus>("offline_status");
}

/** Unlock the offline cache with the master password → read-only items (within TTL). */
export async function offlineUnlock(masterPassword: string): Promise<unknown[]> {
  return invoke<unknown[]>("offline_unlock", { masterPassword });
}

/** Wipe the offline cache. */
export async function offlineWipe(): Promise<void> {
  await invoke("offline_wipe");
}

/** The current offline-cache directory (configured override or default). */
export async function offlineGetDir(): Promise<string> {
  return invoke<string>("offline_get_dir");
}

/** Set the offline-cache directory (empty string resets to default). Returns the new directory. */
export async function offlineSetDir(dir: string): Promise<string> {
  return invoke<string>("offline_set_dir", { dir });
}

/** Native folder picker for the offline-cache location; returns the chosen path or null if cancelled. */
export async function pickCacheFolder(current?: string): Promise<string | null> {
  const { open } = await import("@tauri-apps/plugin-dialog");
  const picked = await open({ directory: true, multiple: false, defaultPath: current });
  return typeof picked === "string" ? picked : null;
}

/** Online revocation check; wipes if the device is revoked / wipe-requested. */
export async function offlineCheck(): Promise<{ revoked: boolean; wipe: boolean; wiped: boolean }> {
  return invoke("offline_check");
}

// --- Desktop self-update (§13): notify + 1-click download from the platform ---

/** The running desktop app's version (empty on web). */
export async function desktopAppVersion(): Promise<string> {
  if (!isTauri()) return "";
  try {
    return await invoke<string>("desktop_app_version");
  } catch {
    return "";
  }
}

/** A stable per-install identifier for this desktop (empty on web), sent on login so the backend
 *  reuses the same device row across re-logins and per-device settings (offline cache) persist. */
export async function deviceClientId(): Promise<string> {
  if (!isTauri()) return "";
  try {
    return await invoke<string>("device_client_id");
  } catch {
    return "";
  }
}

/** Reveal a saved file in the OS file manager. */
export async function revealPath(path: string): Promise<void> {
  try {
    await invoke("reveal_path", { path });
  } catch {
    /* ignore */
  }
}

/** Subscribe to a Tauri event; returns an unlisten function. */
export async function onEvent<T>(event: string, handler: (payload: T) => void): Promise<() => void> {
  const { listen } = await import("@tauri-apps/api/event");
  return listen<T>(event, (e) => handler(e.payload));
}
